import {Avatar, Card, Divider, Flex, Select, Skeleton, Tag, theme, Typography} from 'antd';
import React, {useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import AdminDetailListItem from '../AdminMemberProfile/components/AdminDetailListItem';
import {setCurrentMemberData} from 'Redux/Slices/selectedMember';
import ResetPasswordAndWebenable from './ResetPasswordAndWebenable';
import {searchSRByTypeRecent} from 'Redux/Slices/RecentSRList';
import {searchSRByTypePayload} from 'Utils/helpers/SRPayloadGenerator';
import {searchSRByTypeArchived} from 'Redux/Slices/ArchivedSRList';
import {searchSRByTypeCashback} from 'Redux/Slices/CashbackSRList';
import {globalConfig} from 'Config/config';
import createAvatar from 'Utils/helpers/CreateAvatar';
import getAvatarColor from 'Utils/helpers/GetAvatarColor';

const {Text} = Typography;

// Status -> badge color, matching the mockup's badge-success/warning/error/
// neutral vocabulary. Falls back to neutral for any status string not
// recognized here rather than guessing.
const statusTagColor = (status?: string): 'success' | 'error' | 'warning' | 'default' => {
  const s = (status || '').toLowerCase();
  // Negative states first: "inactive" contains "active", so checking active
  // first painted inactive members green (same order as AdminMemberProfile).
  if (['inactive', 'cancel', 'block'].some((k) => s.includes(k))) return 'error';
  if (s.includes('suspend') || s.includes('pending')) return 'warning';
  if (s.includes('active')) return 'success';
  return 'default';
};

const leftPannelData = [
  {
    key: 'memberNumber',
    value: 'Member#',
  },
  {
    key: 'externalRefId',
    value: 'External Ref#',
  },
  {
    key: 'status',
    value: 'Status',
  },
  {
    key: 'salutation',
    value: 'Salutation',
  },
  {
    key: 'namePrefix',
    value: 'Title',
  },
  {
    key: 'firstName',
    value: 'First Name',
  },
  {
    key: 'lastName',
    value: 'Last Name',
  },
  {
    key: 'clientEmail',
    value: 'Email Address',
  },

  {
    key: 'dateOfBirth',
    value: 'D.O.B',
  },
  {
    key: 'age',
    value: 'Age',
  },
  {
    key: 'addressLine1',
    value: 'Address 1',
  },
  {
    key: 'addressLine2',
    value: 'Address 2',
  },
  {
    key: 'postalCode',
    value: 'Post Code',
  },
  {
    key: 'country',
    value: 'Country',
  },
  {
    key: 'packageName',
    value: 'Package',
  },
  {
    key: 'joinDate',
    value: 'Join Date',
  },
  // {
  //   key: 'isWebEnabled',
  //   value: 'Web Enabled',
  //   type: 'checkbox',
  // },
  {
    key: 'webEnabledDate',
    value: 'Onboarding Date',
  },
  {
    key: 'isVulnerable',
    value: 'Vulnerable',
  },
];

const isMemberCancelled = (memberData: any) => {
  if (memberData?.membershipStatus?.toLowerCase() === 'cancelled') {
    return true;
  } else {
    return false;
  }
};

const MemberDetailsPanel = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const member = useSelector((state: any) => state?.selectedMember?.selectedMember);
  const currentTenantID = useSelector((state: any) => state?.login?.tenantID);

  // Check if the current tenant is allowed to have cancelled member status check
  const isCancelMemberCheckAllowed =
    globalConfig?._config?.cancelMemberStatusCheckAllowedTenants?.includes(
      Number(currentTenantID),
    ) || false;

  const focusedMember = [
    {
      name:
        member?.focusedMemberDetails['firstName'] + ' ' + member.focusedMemberDetails['lastName'],
      memberNumber: member?.focusedMemberDetails?.memberId,
      membershipStatus: member?.focusedMemberDetails?.memberMembershipStatus,
      status: member?.focusedMemberDetails?.status,
      memberData: member?.focusedMemberDetails,
    },
  ];
  const jointMembers = (member?.jointMemberDetails || []).map((obj) => ({
    name: `${obj.firstName} ${obj.lastName}`,
    memberNumber: obj.memberId,
    membershipStatus: obj.memberMembershipStatus,
    status: obj.status,
    memberData: obj,
  }));

  const avaiableMembers = [member.focusedMemberDetails, ...(member?.jointMemberDetails || [])];
  const members1 = [...focusedMember, ...jointMembers];
  const members = members1.map((item) => item['memberNumber']);
  const [selectedMember, setSelectedMember] = useState(members[0]);
  const loginDetails = useSelector((state: any) => state?.login);

  const handleChange = async (value) => {
    const selectedMemeberObj: any = members1.filter((a) => a.memberNumber === value);

    // Prevent selection of cancelled members only if tenant is in allowed list
    if (isCancelMemberCheckAllowed && isMemberCancelled(selectedMemeberObj[0])) {
      return;
    }

    setLoading(true);

    const finalMember = avaiableMembers.filter(
      (a) => a.memberId === selectedMemeberObj[0].memberNumber,
    );
    const updatedMemberData = {
      ...member,
      currentMemberDetails: finalMember[0],
    };

    setLoading(false);
    Promise.all([
      dispatch(setCurrentMemberData(finalMember[0])),
      dispatch(
        searchSRByTypeRecent(searchSRByTypePayload(updatedMemberData, 'recent', loginDetails)),
      ),
      dispatch(
        searchSRByTypeArchived(searchSRByTypePayload(updatedMemberData, 'archive', loginDetails)),
      ),
      dispatch(
        searchSRByTypeCashback(searchSRByTypePayload(updatedMemberData, 'cashback', loginDetails)),
      ),
    ]);

    setSelectedMember(value);
    // setSelectedOption(event.target); // Update the selected option
  };

  if (loading) return <Skeleton active avatar paragraph={{rows: 5}} />;

  const currentDetails = member?.currentMemberDetails ?? member?.focusedMemberDetails ?? {};
  const displayName =
    [currentDetails?.firstName, currentDetails?.lastName].filter(Boolean).join(' ') || '-';
  const displayStatus = currentDetails?.status || currentDetails?.membershipStatus;

  return (
    <MemberSidebarBody
      displayName={displayName}
      displayStatus={displayStatus}
      selectedMember={selectedMember}
      handleChange={handleChange}
      members1={members1}
      isMemberCancelled={isMemberCancelled}
      isCancelMemberCheckAllowed={isCancelMemberCheckAllowed}
      leftPannelData={leftPannelData}
      member={member}
    />
  );
};

// Split out so the token-driven styling below reads cleanly against the
// data-wiring above — same component, same file, purely presentational.
const MemberSidebarBody = ({
  displayName,
  displayStatus,
  selectedMember,
  handleChange,
  members1,
  isMemberCancelled,
  isCancelMemberCheckAllowed,
  leftPannelData: fields,
  member,
}: any) => {
  const {token}: any = theme.useToken();

  return (
    <div
      style={{
        width: 272,
        flexShrink: 0,
        // Sticky positioning + the viewport-height/scroll cap are applied by
        // the parent (Pages/Dashboard/index.tsx wraps this in its own
        // `position:sticky; top:60; height:calc(100vh - 60px); overflowY:auto`
        // div) — not duplicated here.
        margin: '0.75rem 10px',
      }}>
      <Card styles={{body: {padding: 20}}} bordered style={{borderRadius: token.borderRadiusLG}}>
        <Flex align="center" gap={12} style={{marginBottom: 14}}>
          <Avatar
            size={44}
            style={{
              backgroundColor: getAvatarColor(displayName),
              fontSize: 15,
              fontWeight: 600,
              flexShrink: 0,
            }}>
            {createAvatar(displayName)}
          </Avatar>
          <div style={{minWidth: 0}}>
            <Text strong style={{fontSize: 15, display: 'block'}} ellipsis={{tooltip: displayName}}>
              {displayName}
            </Text>
            {displayStatus && (
              <Tag color={statusTagColor(displayStatus)} style={{marginTop: 2}}>
                {displayStatus}
              </Tag>
            )}
          </div>
        </Flex>
        <Divider style={{margin: '4px 0 14px'}} />
        <ResetPasswordAndWebenable />
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: '10px',
          }}>
          <Text style={{width: 100}}>{'Member'}</Text>

          <Select
            size="small"
            style={{width: 212, marginLeft: 9}}
            value={selectedMember}
            onChange={handleChange}
            options={members1.map((option) => {
              const isCancelled = isMemberCancelled(option);
              const shouldDisable = isCancelMemberCheckAllowed && isCancelled;
              return {
                value: option.memberNumber,
                disabled: shouldDisable,
                label: (
                  <span style={{opacity: shouldDisable ? 0.5 : 1}}>
                    <span style={{textDecoration: shouldDisable ? 'line-through' : 'none'}}>
                      {option.name}
                    </span>
                    {shouldDisable && (
                      <Text type="secondary" italic style={{marginLeft: 4}}>
                        (Cancelled)
                      </Text>
                    )}
                  </span>
                ),
              };
            })}
          />
        </div>
        {fields.map((item, index) => {
          if (item.key in member?.currentMemberDetails) {
            return (
              <div key={item.key} style={{marginBottom: 8}}>
                <AdminDetailListItem
                  compact
                  label={item.value}
                  value={member?.currentMemberDetails?.[item?.key] ?? ''}
                  // type={item?.type}
                />
              </div>
            );
          } else {
            return (
              <div key={item.key} style={{marginBottom: 8}}>
                <AdminDetailListItem
                  compact
                  label={item.value}
                  value={member?.[item?.key] ?? ''}
                  // type={item?.type}
                />
              </div>
            );
          }
        })}
      </Card>
    </div>
  );
};

export default MemberDetailsPanel;
